import React from 'react'
import { When } from 'react-if'
import { EpisodesHorizontalScroller } from '@/organisms/EpisodesHorizontalScroller'
import { useGuildMember } from '@/services/GuildUserService/GuildUserService'
import { ProjectType, Season, WatchableNode } from '@/services/ProjectsService'
import { ContentSeason } from '@/types/codegen-federation'
import { episodesToMedias, watchablesEpisodesToMedias } from '@/utils/EpisodeUtil/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { isDefined } from '@/utils/primitives/generics'
import { getProjectTheme } from '@/utils/project-themes'
import { hasSeasonEarlyAccess } from '@/utils/users/users'
import { EpisodeGrid } from './EpisodeGrid'

/**
 * Safely converts ContentEpisode[] to WatchableNode[] for use with watchablesEpisodesToMedias.
 * This type assertion is safe because ContentEpisode extends the WatchableNode interface.
 * The watchablesEpisodesToMedias function filters for ContentEpisode types internally anyway.
 */
const convertContentEpisodesToWatchableNodes = (episodes: ContentSeason['episodes']): WatchableNode[] => {
  return (episodes?.edges?.map((edge) => edge?.node).filter(isDefined) || []) as WatchableNode[]
}

export interface EpisodeSelectorProps {
  projectSlug: string
  projectName?: string
  userGroups?: { name: string }[]
  selectedSeason?: Season
  selectedContentSeason?: ContentSeason
  display?: 'grid' | 'carousel'
  displayDescription?: boolean
  loading?: boolean
  hasError?: boolean
  refetch?: () => void
  projectType?: ProjectType
}

export const EpisodesSelector: React.FC<EpisodeSelectorProps> = ({
  display = 'grid',
  projectSlug,
  selectedSeason,
  selectedContentSeason,
  loading,
  hasError,
  refetch,
  displayDescription = true,
  projectName,
  userGroups,
  projectType,
}) => {
  const { locale } = useLocale()
  const projectTheme = getProjectTheme(projectSlug)
  const { guildPermissions } = useGuildMember()
  const userHasGuildEarlyAccess = guildPermissions?.hasEarlyAccess

  const hasEarlyAccess =
    hasSeasonEarlyAccess(projectTheme.watch?.seasonEarlyAccessGroup, userGroups) || userHasGuildEarlyAccess

  // Handle both old Season and new ContentSeason types
  const medias = selectedContentSeason
    ? // For new Content API: use type-safe conversion helper
      watchablesEpisodesToMedias(convertContentEpisodesToWatchableNodes(selectedContentSeason.episodes), {
        hasEarlyAccess,
        displayDescription,
        projectName,
        projectType,
        locale,
      })
    : // For old Season API: use existing function
      episodesToMedias(selectedSeason?.episodes || [], {
        hasEarlyAccess,
        displayDescription,
        projectName,
        projectType,
        locale,
      })

  return (
    <>
      <When condition={loading}>
        <div className="h-[2.25rem] w-[6.25rem] animate-pulse rounded bg-black" />
      </When>
      {display === 'grid' ? (
        <EpisodeGrid
          projectSlug={projectSlug}
          medias={medias}
          loading={loading}
          hasError={hasError}
          refetch={refetch}
          userHasEarlyAccess={hasEarlyAccess}
        />
      ) : (
        <EpisodesHorizontalScroller medias={medias} />
      )}
    </>
  )
}
